import {
  LegalPageLayout,
  LegalSection,
  SUPPORT_EMAIL,
} from "./LegalPageLayout";

export function TermsPage() {
  return (
    <LegalPageLayout
      title="Terms of Use"
      description="These terms govern access to the MediCorePay web service and Android app by pharmacy organisations and their authorised users."
    >
      <LegalSection title="1. Acceptance and authority">
        <p>
          By accessing MediCorePay, you agree to these terms and confirm that
          you are authorised by the pharmacy or other organisation whose account
          you use. If you use MediCorePay on behalf of an organisation, you must
          follow that organisation&apos;s policies and administrator
          instructions. Do not use the service if you are not authorised or do
          not agree to these terms.
        </p>
      </LegalSection>

      <LegalSection title="2. The service and Android app">
        <p>
          MediCorePay provides pharmacy business workflows. The Android app is a
          permission-aware owner workspace for dashboard access, sales and
          invoice review, inventory and barcode tools, stock counts, purchasing
          and receiving, supplier management and business reports. Additional
          functions may be available through the web or desktop applications
          according to the organisation&apos;s subscription, configuration and
          user permissions.
        </p>
        <p>
          Features may be added, changed, restricted or retired as the service
          is maintained. A visible function does not grant permission to use it;
          tenant, store, role and server-side access controls continue to apply.
        </p>
      </LegalSection>

      <LegalSection title="3. Accounts and security">
        <ul className="list-disc space-y-2 pl-6 marker:text-blue-600">
          <li>Provide accurate account information and keep it current.</li>
          <li>
            Keep credentials and devices secure and do not share user accounts.
          </li>
          <li>
            Notify the pharmacy administrator and MediCorePay support promptly
            if you suspect unauthorised access.
          </li>
          <li>
            Administrators are responsible for assigning appropriate roles,
            stores and permissions and for removing access when it is no longer
            needed.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="4. Acceptable use">
        <p>You must not:</p>
        <ul className="list-disc space-y-2 pl-6 marker:text-blue-600">
          <li>
            access another person&apos;s, store&apos;s or tenant&apos;s data
            without authority;
          </li>
          <li>
            circumvent access controls or probe, disrupt or overload the
            service;
          </li>
          <li>
            upload malicious code or use the service for fraud or unlawful
            activity;
          </li>
          <li>
            copy, reverse engineer or resell the service except where expressly
            permitted by law or a written agreement; or
          </li>
          <li>
            use automation in a way that interferes with the service or other
            users.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Pharmacy and professional responsibilities">
        <p>
          MediCorePay is an operational tool, not a substitute for a
          pharmacist&apos;s professional judgement, required verification or
          legal obligations. Pharmacy organisations and users remain responsible
          for product, inventory and receiving accuracy; required approvals;
          patient and prescription decisions; tax and regulatory compliance; and
          reviewing information before acting on it.
        </p>
      </LegalSection>

      <LegalSection title="6. Organisation data and audit records">
        <p>
          Users may enter or approve records on behalf of their organisation.
          The organisation is responsible for having authority to submit that
          information and for setting appropriate access. MediCorePay may
          preserve timestamps, user attribution and other audit details needed
          to maintain the integrity of pharmacy business records, even after an
          individual employee account is deactivated or deleted.
        </p>
        <p>
          Our handling of personal information is described in the{" "}
          <a
            className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
            href="/privacy"
          >
            Privacy Policy
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="7. Connectivity, updates and availability">
        <p>
          Companion features may require a supported device, a current app
          version and an internet connection. Network, device, maintenance or
          third-party infrastructure issues may interrupt access. Do not rely on
          MediCorePay as the only means of carrying out an urgent
          safety-critical or legally required action; maintain appropriate
          business procedures for outages and verify whether a submitted action
          was accepted.
        </p>
      </LegalSection>

      <LegalSection title="8. Ownership and feedback">
        <p>
          MediCorePay and its software, design and service content are protected
          by intellectual property laws. These terms grant only a limited,
          non-transferable right to use the service for the organisation&apos;s
          authorised business purposes. The organisation retains its rights in
          information it submits. Feedback may be used to improve the service
          without an obligation to compensate the person providing it.
        </p>
      </LegalSection>

      <LegalSection title="9. Suspension and termination">
        <p>
          Access may be suspended or ended when required by an organisation
          administrator, subscription status, security risk, suspected misuse,
          legal requirement or material breach of these terms. Ending a
          user&apos;s access does not require deletion of pharmacy business
          records that must be retained for audit, contractual, tax, regulatory
          or legal reasons.
        </p>
      </LegalSection>

      <LegalSection title="10. Disclaimers and responsibility">
        <p>
          To the extent permitted by applicable law, the service is provided on
          an “as available” basis. MediCorePay does not guarantee uninterrupted
          or error-free operation. Users and organisations are responsible for
          checking records and outputs before relying on them. Nothing in these
          terms excludes or limits rights or responsibilities that cannot
          lawfully be excluded or limited.
        </p>
      </LegalSection>

      <LegalSection title="11. Changes and contact">
        <p>
          We may update these terms as the service or legal requirements change.
          The effective and last-updated date at the top identifies the current
          version. Continued use after an update means the updated terms apply,
          subject to any notice or consent required by law.
        </p>
        <p>
          Questions about these terms can be sent to{" "}
          <a
            className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
            href={`mailto:${SUPPORT_EMAIL}?subject=MediCorePay%20terms%20question`}
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
